import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, Avatar, TablePagination, useMediaQuery, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import { useNavigate } from 'react-router-dom';
import { Person } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState, useCallback } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from '../shared/BulkActions';
import { CSVExport, type ColumnDef } from '../shared/CSVExport';
import { TutorImport } from './TutorImport';
import type { TutorRecord } from '../shared/types';

const STATUSES = ['Draft', 'Active', 'Inactive', 'Suspended'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
  Suspended: 'error',
};

const TUTOR_COLUMNS: ColumnDef[] = [
  { field: 'employee.employeeNumber', header: 'Employee #' },
  { field: 'employmentRelationship', header: 'Employment Relationship' },
  { field: 'status', header: 'Status' },
];

export const TutorList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { data: allTutors, isLoading } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const tutors = allTutors?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allTutors?.length ?? 0,
    active: allTutors?.filter((t: TutorRecord) => t.status === 'Active').length ?? 0,
    inactive: allTutors?.filter((t: TutorRecord) => t.status === 'Inactive').length ?? 0,
    suspended: allTutors?.filter((t: TutorRecord) => t.status === 'Suspended').length ?? 0,
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) ids.forEach(id => next.add(id));
      else ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Tutors"
          action={<TutorImport />}
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
            { label: 'Suspended', value: String(stats.suspended), color: 'error.main' },
          ]}
        />
        <BulkActions
          selectedIds={Array.from(selectedIds)}
          resourceName="tutors"
          selectedCount={selectedIds.size}
          onClearSelection={clearSelection}
          onStatusChange={clearSelection}
          onExportSelected={() => {}}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {tutors?.map((tutor: TutorRecord) => (
            <Paper key={tutor.id} sx={{ p: 2 }} onClick={() => navigate(`/tutors/${tutor.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    {tutor.legalEntity?.firstName?.[0] ?? 'T'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {tutor.legalEntity?.firstName || tutor.legalEntity?.lastName
                        ? `${tutor.legalEntity?.firstName ?? ''} ${tutor.legalEntity?.lastName ?? ''}`.trim()
                        : 'Unnamed Tutor'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{tutor.employmentRelationship}</Typography>
                  </Box>
                </Box>
                <Chip label={tutor.status} size="small" color={STATUS_COLORS[tutor.status]} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Tutors"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TutorImport />
            {allTutors && (
              <CSVExport
                data={allTutors}
                columns={TUTOR_COLUMNS}
                filename="tutors.csv"
                selectedIds={selectedIds.size > 0 ? Array.from(selectedIds) : undefined}
              />
            )}
          </Box>
        }
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'error.main' },
        ]}
      />
      <BulkActions
        selectedIds={Array.from(selectedIds)}
        resourceName="tutors"
        selectedCount={selectedIds.size}
        onClearSelection={clearSelection}
        onStatusChange={clearSelection}
        onExportSelected={() => {}}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <SelectAllCheckbox items={tutors ?? []} selectedIds={selectedIds} onToggleAll={toggleAll} />
              <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employee #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employment Relationship</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tutors?.map((tutor: TutorRecord) => (
              <TableRow
                key={tutor.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tutors/${tutor.id}/show`)}
              >
                <SelectionCheckbox id={tutor.id} selected={selectedIds.has(tutor.id)} onToggle={toggleSelect} />
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      {tutor.legalEntity?.firstName?.[0] ?? 'T'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {tutor.legalEntity?.firstName || tutor.legalEntity?.lastName
                          ? `${tutor.legalEntity?.firstName ?? ''} ${tutor.legalEntity?.lastName ?? ''}`.trim()
                          : 'Unnamed Tutor'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tutor.legalEntity?.email || tutor.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{(tutor as any).employee?.employeeNumber || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{tutor.employmentRelationship}</Typography></TableCell>
                <TableCell><Chip label={tutor.status} size="small" color={STATUS_COLORS[tutor.status]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allTutors?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!tutors || tutors.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Person sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No tutors found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
